import type { Cart, CartLine, CartUpgrade } from "./api";

/**
 * How the cart page lays out a cart whose server response carries upgrades.
 *
 * The server stores the lines the customer chose and reports which of them it
 * is charging as a package (Klasik + Raya as Plus). The page shows each
 * upgrade as one row and each line's leftover as its own row, but every
 * control still has to write the stored line quantities — that is the only
 * thing the cart API accepts. These helpers are that translation, kept pure
 * so it can be tested without a DOM.
 */

export type CartRow =
  | { kind: "upgrade"; upgrade: CartUpgrade }
  | { kind: "line"; line: CartLine; qty: number };

/** A stored quantity to write. Zero means remove the line. */
export interface QtyChange {
  skuId: string;
  qty: number;
}

/** Upgrades first — they are the headline — then whatever lines have left. */
export function cartRows(cart: Cart | null): CartRow[] {
  if (!cart) return [];
  const rows: CartRow[] = (cart.upgrades ?? []).map((upgrade) => ({ kind: "upgrade", upgrade }));
  for (const line of cart.items) {
    const qty = line.qty - (line.upgradedQty ?? 0);
    if (qty > 0) rows.push({ kind: "line", line, qty });
  }
  return rows;
}

/** Items as the customer sees them: a Plus is one desk, not a desk and a stand. */
export function itemCount(cart: Cart | null): number {
  return cartRows(cart).reduce((n, r) => n + (r.kind === "upgrade" ? r.upgrade.qty : r.qty), 0);
}

function storedQty(cart: Cart, skuId: string): number {
  return cart.items.find((i) => i.skuId === skuId)?.qty ?? 0;
}

/** Set a leftover line to `remaining`, keeping the units inside upgrades. */
export function lineQtyChange(line: CartLine, remaining: number): QtyChange {
  return { skuId: line.skuId, qty: Math.max(0, (line.upgradedQty ?? 0) + remaining) };
}

/**
 * Change an upgrade's quantity by `delta`: every source line moves together,
 * by its share per unit. Going to zero removes the upgrade's units only, not
 * any leftover the lines also have.
 */
export function upgradeQtyChanges(cart: Cart, upgrade: CartUpgrade, delta: number): QtyChange[] {
  return upgrade.sources.map((s) => ({
    skuId: s.skuId,
    qty: Math.max(0, storedQty(cart, s.skuId) + delta * s.qty),
  }));
}

/**
 * Take one part out of an upgrade — "Hapus dudukan monitor". The rest stay
 * in the cart and the server stops upgrading them, so a Plus becomes the
 * Klasik the customer is left with.
 */
export function removeSourceChange(cart: Cart, upgrade: CartUpgrade, skuId: string): QtyChange {
  const share = upgrade.sources.find((s) => s.skuId === skuId)?.qty ?? 0;
  return { skuId, qty: Math.max(0, storedQty(cart, skuId) - upgrade.qty * share) };
}

/** Pasang on an upgrade is the desk's: every installable source line flips. */
export function upgradePasangTargets(cart: Cart, upgrade: CartUpgrade): string[] {
  return upgrade.sources
    .filter((s) => cart.items.find((i) => i.skuId === s.skuId)?.pasangEligible)
    .map((s) => s.skuId);
}

/**
 * Upgrades an add created: those the cart has after it and did not have, or
 * had fewer of, before. The product page shows their notice at "Ditambahkan",
 * so a customer who adds a Raya with a Klasik already in the cart hears about
 * the Plus price there, not only on the cart page.
 */
export function newUpgrades(before: Cart | null, after: Cart): CartUpgrade[] {
  const had = new Map((before?.upgrades ?? []).map((u) => [u.skuId, u.qty]));
  return (after.upgrades ?? []).filter((u) => u.qty > (had.get(u.skuId) ?? 0));
}
